import {
  createContext,
  Dispatch,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer
} from 'react';
import { AUTH_ACTION_TYPE, SPINNER } from 'enums';
import {
  AUTH_SCREEN,
  EMAIL_VERIFICATION_COOL_DOWN,
  PLATFORMS
} from 'constants/general';
import { PARAMS } from 'constants/params';
import { AuthAction, AuthState } from 'interfaces/auth.interface';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  updateIsNewUserSigninProcess,
  updatePlatform
} from 'store/reducer/setting/slice';
import { toast } from 'react-toastify';
import { isDesktopLoginRequest, login } from 'services/auth.service';
import OverlaySpinner from 'components/OverlaySpinner';
import { authActionsWrapperSelector } from 'store/reducer/selectors/auth.selectors';
import { getQueryParam } from 'utils/routes.util';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { capturePostHogEvent } from 'services/posthog.service';
import { POST_HOG_EVENT } from 'constants/events';
import { updateUserConsents } from 'store/reducer/user/extra';
import { DEFAULT_USER_CONSENTS } from 'assets/data';
import { isMobileDevice } from 'utils/device.util';
import { isValidEmail as validateEmailAddress } from 'utils/email.util';
import { isSupportPanelPage } from 'utils/support-panel.util';

const uiQueryParam = getQueryParam(PARAMS.UI) ?? AUTH_SCREEN.SIGN_IN;
// The support panel never offers sign-up, so the query parameter cannot select a screen there.
const initialAuthScreen =
  !isSupportPanelPage() && Object.values(AUTH_SCREEN).includes(uiQueryParam)
    ? uiQueryParam
    : AUTH_SCREEN.SIGN_IN;

const initAuthState: AuthState = {
  screen: initialAuthScreen,
  email: '',
  password: '',
  newPassword: '',
  isValidEmail: false,
  isValidPassword: false,
  isProcessing: false,
  isEmailVerified: true,
  coolDown: EMAIL_VERIFICATION_COOL_DOWN.START,
  googleLoginInProgress: false,
  appleLoginInProgress: false,
  hasAcceptedTerms: false,
  hasPasswordResetSucceeded: false
};

const reducer = (state: AuthState, action: AuthAction) => {
  switch (action.type) {
    case AUTH_ACTION_TYPE.NAVIGATE_TO_SIGN_IN:
      return { ...state, screen: AUTH_SCREEN.SIGN_IN };
    case AUTH_ACTION_TYPE.NAVIGATE_TO_SIGN_UP:
      return { ...state, screen: AUTH_SCREEN.SIGN_UP };
    case AUTH_ACTION_TYPE.NAVIGATE_TO_FORGOT_PASSWORD:
      return { ...state, screen: AUTH_SCREEN.FORGOT_PASSWORD };
    case AUTH_ACTION_TYPE.UPDATE_EMAIL: {
      const email = (action.value as string) ?? '';
      return { ...state, email, isValidEmail: validateEmailAddress(email) };
    }
    case AUTH_ACTION_TYPE.UPDATE_PASSWORD: {
      const password = (action.value as string) ?? '';
      return { ...state, password, isValidPassword: Boolean(password) };
    }
    case AUTH_ACTION_TYPE.UPDATE_NEW_PASSWORD:
      return { ...state, newPassword: (action.value as string) ?? '' };
    case AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING:
      return { ...state, isProcessing: (action.value as boolean) ?? false };
    case AUTH_ACTION_TYPE.UPDATE_IS_EMAIL_VERIFIED:
      return { ...state, isEmailVerified: (action.value as boolean) ?? false };
    case AUTH_ACTION_TYPE.UPDATE_COOL_DOWN:
      return {
        ...state,
        coolDown: (action.value as number) ?? EMAIL_VERIFICATION_COOL_DOWN.START
      };
    case AUTH_ACTION_TYPE.UPDATE_GOOGLE_LOGIN_IN_PROGRESS:
      return {
        ...state,
        googleLoginInProgress: (action.value as boolean) ?? false
      };
    case AUTH_ACTION_TYPE.UPDATE_APPLE_LOGIN_IN_PROGRESS:
      return {
        ...state,
        appleLoginInProgress: (action.value as boolean) ?? false
      };
    case AUTH_ACTION_TYPE.UPDATE_HAS_ACCEPTED_TERMS:
      return {
        ...state,
        hasAcceptedTerms: (action.value as boolean) ?? false
      };
    case AUTH_ACTION_TYPE.REGISTRATION_COMPLETED:
      return {
        ...state,
        password: state.newPassword
      };
    case AUTH_ACTION_TYPE.UPDATE_HAS_PASSWORD_RESET_SUCCEEDED:
      return {
        ...state,
        hasPasswordResetSucceeded: (action.value as boolean) ?? false
      };
    default:
      return state;
  }
};

export const AuthPageContext = createContext<{
  state: AuthState;
  dispatch?: Dispatch<AuthAction>;
}>({ state: initAuthState });

const AuthActionsWrapper = ({ children }: PropsWithChildren<object>) => {
  const [state, dispatch] = useReducer(reducer, initAuthState);
  const appDispatch = useAppDispatch();
  const { isNewUserSigninProcess, language } = useAppSelectorShallow(
    authActionsWrapperSelector
  );
  const navigate = useNavigate();

  useEffect(() => {
    const platform = getQueryParam(PARAMS.PLATFORM);
    if (platform && Object.values(PLATFORMS).includes(platform)) {
      appDispatch(updatePlatform(platform));
    }
  }, [appDispatch]);

  // Track when users land on specific auth screens
  useEffect(() => {
    if (state.screen === AUTH_SCREEN.SIGN_UP) {
      capturePostHogEvent(POST_HOG_EVENT.AUTH_PAGE.SIGNUP_SCREEN_LANDED);
    } else if (state.screen === AUTH_SCREEN.SIGN_IN) {
      capturePostHogEvent(POST_HOG_EVENT.AUTH_PAGE.SIGNIN_SCREEN_LANDED);
    }
  }, [state.screen]);

  useEffect(() => {
    if (
      isNewUserSigninProcess &&
      !state.googleLoginInProgress &&
      !state.appleLoginInProgress
    ) {
      handleNewAccountAutoLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isNewUserSigninProcess,
    state.googleLoginInProgress,
    state.appleLoginInProgress
  ]);

  const handleNewAccountAutoLogin = useCallback(async () => {
    const { email, newPassword } = state;
    await login(email, newPassword, (hasError, error) => {
      if (hasError) {
        appDispatch(updateIsNewUserSigninProcess(false));
        toast.error(error);
        dispatch({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING });
      } else {
        appDispatch(updateUserConsents(DEFAULT_USER_CONSENTS));
        const nextNonMobileRoute = isDesktopLoginRequest
          ? `${ROUTES.DESKTOP_REDIRECT}?${PARAMS.SIGN_INTO_DESKTOP}=true`
          : ROUTES.DOWNLOAD_APPS;
        const isMobile = isMobileDevice();
        const targetRoute = isMobile
          ? ROUTES.MOBILE_APP_DOWNLOAD
          : nextNonMobileRoute;

        navigate(targetRoute, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.email, state.newPassword]);

  return (
    <AuthPageContext.Provider
      value={useMemo(() => ({ state, dispatch }), [state])}>
      <section
        key={language}
        className='w-[96%] lg:w-[60%] flex items-center justify-center bg-secondary-50 dark:bg-secondary-dark-300 dark:border dark:border-secondary-dark-500 ~rounded-[2rem]/[5rem] ~px-4/10 ~py-5/8 shadow'>
        {children}
        {isNewUserSigninProcess && (
          <OverlaySpinner
            type={SPINNER.DOT_WAVE}
            title={t('auth_page.welcome_your_account_is_ready_signing_you_in')}
          />
        )}
      </section>
    </AuthPageContext.Provider>
  );
};

export default AuthActionsWrapper;
